import React from 'react';
import { Box, Checkbox, Stack, Typography } from '@mui/material';
import type { FeatureFlagTargetClientSelectorProps } from '../../../types/features/featureFlags';
import { adminColors, adminTypography } from '../../../styles/adminDesignTokens';
import { FEATURE_FLAG_CLIENT_OPTIONS } from '../constants/featureFlagOptions';

const FeatureFlagTargetClientSelector: React.FC<FeatureFlagTargetClientSelectorProps> = ({
  activeClient,
  configurations,
  onActiveClientChange,
  onClientSelectedChange,
  selectionDisabled = false,
  targetClientError,
}) => (
  <Box>
    <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text, mb: 1 }}>
      Target Clients
    </Typography>
    <Box
      sx={{
        display: 'grid',
        gap: 1.25,
        gridTemplateColumns: selectionDisabled ? '1fr' : { xs: '1fr', sm: '1fr 1fr' },
      }}
    >
      {FEATURE_FLAG_CLIENT_OPTIONS
        .filter((option) => !selectionDisabled || option.value === activeClient)
        .map((option) => {
          const selected = configurations[option.value].selected;
          const active = activeClient === option.value;
          const cardDisabled = selectionDisabled;
          const helperText = selectionDisabled
            ? 'Target client cannot be changed after creation.'
            : selected
              ? `This feature will be created for ${option.label} only.`
              : `Select ${option.label} as the target client.`;

          return (
            <Box
              key={option.value}
              onClick={() => {
                if (!cardDisabled) {
                  if (selected) {
                    onActiveClientChange(option.value);
                  } else {
                    onClientSelectedChange(option.value, true);
                  }
                }
              }}
              sx={{
                backgroundColor: active ? adminColors.primarySoft : adminColors.surface,
                border: `1px solid ${active ? adminColors.primary : adminColors.border}`,
                borderRadius: '8px',
                cursor: cardDisabled ? 'default' : 'pointer',
                minHeight: 76,
                p: 1.25,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Checkbox
                  checked={selected}
                  disabled={selectionDisabled}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => onClientSelectedChange(option.value, event.target.checked)}
                  size="small"
                  sx={{ color: adminColors.primary, p: 0.25 }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
                    {option.label}
                  </Typography>
                  <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText, mt: 0.25 }}>
                    {helperText}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          );
        })}
    </Box>
    {targetClientError ? (
      <Typography sx={{ ...adminTypography.helper, color: adminColors.danger, mt: 0.75 }}>
        {targetClientError}
      </Typography>
    ) : null}
  </Box>
);

export default React.memo(FeatureFlagTargetClientSelector);
